import { useCallback } from 'react'

import { Text } from '@cash-me/react-components'
import NextLink from 'next/link'

import { AccordionNoRadix } from 'components/accordionNoRadix'
import { Grid, Col } from 'components/grid'
import { Logo } from 'components/logo'
import useWindowSize from 'hooks/useWindowSize'
import { LinkProps } from 'src/models'
import { pushDataLayer } from 'utils/push-data-layer'

import { products, moreProducts, calculators, otherProducts, cashme, partners, help } from './data'
import * as S from './styles'

const Quicklinks = () => {
  const { isMobile } = useWindowSize()

  const renderLink = ({ id, link, label, dataLayer }: LinkProps) => (
    <li key={id}>
      <NextLink href={link} onClick={() => pushDataLayer(dataLayer)} className="ds-link">
        {label}
      </NextLink>
    </li>
  )

  const handleDataLayer = useCallback((name: string) => {
    pushDataLayer({
      category: 'Clique',
      action: 'Footer',
      label: `Redes Sociais - ${name}`
    })
  }, [])

  const handleContactButton = useCallback(() => {
    window.location.href = '/fale-conosco/#contact-form'
  }, [])

  return (
    <S.Quicklinks $bg="contrast">
      <Grid col={{ phone: 1, tablet: 12, desktop: 12 }}>
        <Col col={{ tablet: 12, desktop: 10 }} start={{ desktop: 2 }}>
          <S.LogoWrapper>
            <NextLink href="/" aria-label="CashMe">
              <Logo type="CashmeFooter" width={9.375} height={2} />
            </NextLink>
          </S.LogoWrapper>
        </Col>
        {isMobile ? (
          <Col col={{ phone: 1 }}>
            <div className="accordion-support accordion-background">
              <AccordionNoRadix
                id="quicklinks-0"
                title={
                  <Text as="span" color="var(--colors-blue-text-strong)" weight="medium">
                    Nossos Produtos
                  </Text>
                }
                className="accordion-background"
              >
                <S.AccordionLinks>{products.map(renderLink)}</S.AccordionLinks>
              </AccordionNoRadix>
              <AccordionNoRadix
                id="quicklinks-1"
                title={
                  <Text as="span" color="var(--colors-blue-text-strong)" weight="medium">
                    Como podemos te ajudar?
                  </Text>
                }
                className="accordion-background"
              >
                <S.AccordionLinks>{moreProducts.map(renderLink)}</S.AccordionLinks>
              </AccordionNoRadix>
              <AccordionNoRadix
                id="quicklinks-2"
                title={
                  <Text as="span" color="var(--colors-blue-text-strong)" weight="medium">
                    Calculadoras
                  </Text>
                }
                className="accordion-background"
              >
                <S.AccordionLinks>{calculators.map(renderLink)}</S.AccordionLinks>
              </AccordionNoRadix>
              <AccordionNoRadix
                id="quicklinks-3"
                title={
                  <Text as="span" color="var(--colors-blue-text-strong)" weight="medium">
                    Veja também
                  </Text>
                }
                className="accordion-background"
              >
                <S.AccordionLinks>{otherProducts.map(renderLink)}</S.AccordionLinks>
              </AccordionNoRadix>
              <AccordionNoRadix
                id="quicklinks-4"
                title={
                  <Text as="span" color="var(--colors-blue-text-strong)" weight="medium">
                    A CashMe
                  </Text>
                }
                className="accordion-background"
              >
                <S.AccordionLinks>{cashme.map(renderLink)}</S.AccordionLinks>
              </AccordionNoRadix>
              <AccordionNoRadix
                id="quicklinks-5"
                title={
                  <Text as="span" color="var(--colors-blue-text-strong)" weight="medium">
                    Parceiros CashMe
                  </Text>
                }
                className="accordion-background"
              >
                <S.AccordionLinks>{partners.map(renderLink)}</S.AccordionLinks>
              </AccordionNoRadix>
              <AccordionNoRadix
                id="quicklinks-6"
                title={
                  <Text as="span" color="var(--colors-blue-text-strong)" weight="medium">
                    Precisa de Ajuda?
                  </Text>
                }
                className="accordion-background"
              >
                <S.AccordionLinks>{help.map(renderLink)}</S.AccordionLinks>
              </AccordionNoRadix>
              <Text size="heading5" color="var(--colors-blue-text-strong)" weight="bold" css={{ marginTop: '1.5rem' }}>
                Entre em contato
              </Text>
              <S.ContactButton
                type="anchor"
                href="/fale-conosco/#contact-form"
                size="md"
                dataLayer={{
                  category: 'Clique',
                  action: 'Ajuda',
                  label: 'Botão Fale com nosso atendimento'
                }}
                onClick={handleContactButton}
              >
                Fale com nosso atendimento
              </S.ContactButton>
            </div>
          </Col>
        ) : (
          <Col col={{ tablet: 12, desktop: 10 }} start={{ desktop: 2 }}>
            <S.Links>
              <S.LinksSection>
                <Text as="span" size="heading5" color="var(--colors-blue-text-strong)" weight="bold">
                  Nossos Produtos
                </Text>
                <S.LinkList>{products.map(renderLink)}</S.LinkList>
              </S.LinksSection>
              <S.LinksSection>
                <Text as="span" size="heading5" color="var(--colors-blue-text-strong)" weight="bold">
                  Como podemos te ajudar?
                </Text>
                <S.LinkList>{moreProducts.map(renderLink)}</S.LinkList>
              </S.LinksSection>
              <S.LinksSection>
                <Text as="span" size="heading5" color="var(--colors-blue-text-strong)" weight="bold">
                  Calculadoras
                </Text>
                <S.LinkList>{calculators.map(renderLink)}</S.LinkList>
              </S.LinksSection>
              <S.LinksSection>
                <Text as="span" size="heading5" color="var(--colors-blue-text-strong)" weight="bold">
                  Veja também
                </Text>
                <S.LinkList>{otherProducts.map(renderLink)}</S.LinkList>
              </S.LinksSection>
            </S.Links>
            <hr />
            <S.Links>
              <S.LinksSection>
                <Text as="span" size="heading5" color="var(--colors-blue-text-strong)" weight="bold">
                  A CashMe
                </Text>
                <S.LinkList>{cashme.map(renderLink)}</S.LinkList>
              </S.LinksSection>
              <S.LinksSection>
                <Text as="span" size="heading5" color="var(--colors-blue-text-strong)" weight="bold">
                  Parceiros CashMe
                </Text>
                <S.LinkList>{partners.map(renderLink)}</S.LinkList>
              </S.LinksSection>
              <S.LinksSection>
                <Text as="span" size="heading5" color="var(--colors-blue-text-strong)" weight="bold">
                  Precisa de Ajuda?
                </Text>
                <S.LinkList>{help.map(renderLink)}</S.LinkList>
              </S.LinksSection>
              <S.LinksSection>
                <Text as="span" size="heading5" color="var(--colors-blue-text-strong)" weight="bold">
                  Entre em contato
                </Text>
                <S.ContactButton
                  type="anchor"
                  href="/fale-conosco/#contact-form"
                  size="md"
                  dataLayer={{
                    category: 'Clique',
                    action: 'Ajuda',
                    label: 'Botão Fale com nosso atendimento'
                  }}
                  onClick={handleContactButton}
                >
                  Fale com nosso atendimento
                </S.ContactButton>
              </S.LinksSection>
            </S.Links>
          </Col>
        )}
        <Col col={{ tablet: 12, desktop: 10 }} start={{ desktop: 2 }}>
          <S.Links>
            <S.Social>
              <Text size="body2" color="var(--colors-blue-text-strong)">
                Acompanhe
              </Text>
              <a
                href="https://www.facebook.com/CashMeBrasil/"
                target="_blank"
                onClick={() => handleDataLayer('Facebook')}
                rel="nofollow noreferrer"
                title="Facebook"
              >
                <Logo type="IconFacebook" width={1.625} height={1.563} ariaLabel="Facebook" />
              </a>
              <a
                href="https://www.instagram.com/cashmebrasil/"
                target="_blank"
                onClick={() => handleDataLayer('Instagram')}
                rel="nofollow noreferrer"
                title="Instagram"
              >
                <Logo type="IconInstagram" width={1.625} height={1.563} ariaLabel="Instagram" />
              </a>
              <a
                href="https://www.linkedin.com/company/cashmebrasil/mycompany/"
                target="_blank"
                onClick={() => handleDataLayer('LinkedIn')}
                rel="nofollow noreferrer"
                title="LinkedIn"
              >
                <Logo type="IconLinkedin" width={1.25} height={1.25} ariaLabel="LinkedIn" />
              </a>
              <a
                href="https://www.youtube.com/channel/UCeuoZPiBsF4QyR9kqVpaBog"
                target="_blank"
                onClick={() => handleDataLayer('YouTube')}
                rel="nofollow noreferrer"
                title="YouTube"
              >
                <Logo type="IconYoutube" width={1.625} height={1.563} ariaLabel="YouTube" />
              </a>
            </S.Social>
            <S.Address>
              <Text size="body2" color="var(--colors-blue-text-strong)">
                Rua Olimpíadas, 242, 7º andar - Vila Olímpia São Paulo - São Paulo | CEP 04551-000
              </Text>
            </S.Address>
          </S.Links>
        </Col>
      </Grid>
    </S.Quicklinks>
  )
}

export default Quicklinks
